import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Linkedin, Github, Phone, Twitter } from 'lucide-react';
import profilePhoto from '../assets/alroypereiraProfile.jpeg';

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hoveredSocial, setHoveredSocial] = useState(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const gradientStyle = {
    background: `radial-gradient(
      circle at ${mousePosition.x}% ${mousePosition.y}%,
      rgba(47, 226, 47, 0.1),
      rgba(13, 13, 13, 0.05)
    )`
  };

  const socialLinks = [
    { icon: Mail, label: 'alroypereira05@gmail.com', id: 'email', link: 'mailto:alroypereira05@gmail.com' },
    { icon: Linkedin, label: 'alroypereira', id: 'linkedin', link: 'https://www.linkedin.com/in/alroy-pereira-2b3631248/' },
    { icon: Github, label: 'alroy05', id: 'github', link: 'https://github.com/Alroy05' },
    { icon: Phone, label: '+91 8378990589', id: 'phone', link: 'tel:+918378990589' },
    { icon: Twitter, label: '@alroypereira3', id: 'twitter', link: 'https://x.com/AlroyPereira3' }
  ];

  return (
    <div className="relative font-lato min-h-screen flex items-center justify-center bg-[#0d0d0d] overflow-hidden p-6" id="home">
      <div className="absolute inset-0 transition-all duration-300 ease-out backdrop-blur-sm" style={gradientStyle} />
      
      <div className="relative z-10 max-w-7xl mx-auto w-full flex items-center justify-center px-4">
        <div className="flex flex-col md:flex-row items-center gap-16 max-w-6xl">
          {/* Profile Image Section */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative mt-20 md:mt-0"
          >
            <div className="relative w-64 h-64 md:w-80 md:h-80">
              <img
                src={profilePhoto}
                alt="Profile"
                className="rounded-xl object-cover w-full h-full shadow-lg"
              />
              <div className="absolute bg-[#2fe22f]/10 -bottom-3 -right-3 w-full h-full border-2 border-[#2fe22f] rounded-xl -z-10 backdrop-blur-sm" />
              <div className="absolute bg-[#2fe22f]/5 -bottom-6 -right-6 w-full h-full border border-[#2fe22f]/30 rounded-xl -z-20 backdrop-blur-sm" />
            </div>
          </motion.div>

          {/* Text Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center md:text-left flex-1"
          >
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl md:text-2xl mb-4 text-gray-300"
            >
              <span className="text-[#2fe22f] font-semibold">Hey there!</span> I am
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-5xl md:text-7xl font-bold mb-6"
            >
              <span className="bg-gradient-to-r from-[#2fe22f] via-emerald-300 to-[#2fe22f] text-transparent bg-clip-text bg-[length:200%_auto] font-extrabold">
                Alroy Pereira
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed"
            >
              I am a <span className="text-[#2fe22f] font-semibold">Full Stack Developer</span> with a passion for building and problem solving. As a final-year Computer Engineering student at Fr.CRCE, I am driven by my enthusiasm for technology and innovation. I thrive on tackling new challenges and constantly expanding my skill set. My dedication to finding creative solutions fuels my work, and I have experience with a wide range of programming languages.
            </motion.p>

            {/* Social Links */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="flex flex-wrap gap-4 mt-8 justify-center md:justify-start"
            >
              {socialLinks.map((social) => (
                <a 
                  href={social.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  key={social.id}
                >
                  <motion.div
                    className="relative"
                    onHoverStart={() => setHoveredSocial(social.id)}
                    onHoverEnd={() => setHoveredSocial(null)}
                  >
                    <motion.div 
                      className="bg-[#1a1a1a]/50 backdrop-blur-md p-3 rounded-lg cursor-pointer flex items-center gap-3 border border-gray-800 hover:border-[#2fe22f]/50 transition-colors duration-300"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <social.icon className="w-5 h-5 text-[#2fe22f]" />
                      {hoveredSocial === social.id && (
                        <motion.span
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: 'auto' }}
                          className="text-gray-300 text-sm whitespace-nowrap overflow-hidden"
                        >
                          {social.label}
                        </motion.span>
                      )}
                    </motion.div>
                  </motion.div>
                </a>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Hero;